import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, StarHalf } from "lucide-react";

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  // State declarations
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state for both products and reviews
  const [reviewImageLoading, setReviewImageLoading] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [reviewName, setReviewName] = useState("");
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewImage, setReviewImage] = useState(null);
  const [reviewImagePreview, setReviewImagePreview] = useState(null);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [showCustomizeForm, setShowCustomizeForm] = useState(false);
  
  const [customizeData, setCustomizeData] = useState({
    name: "",
    email: "",
    whatsapp: "",
    message: "",
    colors: "",
    size: "",
    text: "",
    Quantity: "",
    state: "",
    district: "",
    pin: "",
    flat: "",
    apartment: "",
    area: "",
    productName: "",
    instructions: "",
    image: null,
  });
  const [formLoading, setFormLoading] = useState(false);
const [mainImageIndex, setMainImageIndex] = useState(0);
const [popupImage, setPopupImage] = useState(null);
  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch("https://rangdesaaj.onrender.com/api/products");
        if (!res.ok) throw new Error("Failed to fetch products");
        const data = await res.json();
        setFeaturedProducts(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchProducts();
  }, []);

  // When products or productId change, select the product
  useEffect(() => {
    if (featuredProducts.length === 0) return;

    const selectedProduct = featuredProducts.find(
      (p) => p.id === parseInt(productId)
    );

    if (selectedProduct) {
      setProduct(selectedProduct);
      setCustomizeData((prev) => ({
        ...prev,
        productName: selectedProduct.name,
        
      }));
    } else {
      setProduct(null);
    }
    // Set loading to false after product is processed
    setLoading(false);
  }, [featuredProducts, productId]);

  // Fetch reviews for the productId
  useEffect(() => {
    if (!productId) return;

    const fetchReviews = async () => {
      try {
        const res = await fetch(`https://rangdesaaj.onrender.com/api/reviews/${productId}`);
        if (!res.ok) throw new Error("Failed to fetch reviews");
        const data = await res.json();
        setReviews(data);
      } catch (err) {
        console.error(err);
        setReviews([]);
      }
    };

    fetchReviews();
  }, [productId]);

  // Handle rating star click
  const handleRatingClick = (rating) => {
    setReviewRating(rating);
  };

  // Handle review image file input change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReviewImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReviewImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit review handler
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (reviewRating === 0) {
      alert("Please select a rating");
      return;
    }

    let uploadedImageUrl = null;

    if (reviewImage) {
      setReviewImageLoading(true);
      const formData = new FormData();
      formData.append("image", reviewImage);
      try {
        const imageUploadRes = await fetch("https://rangdesaaj.onrender.com/api/upload-image", {
          method: "POST",
          body: formData,
        });
        const imageUploadData = await imageUploadRes.json();
        setReviewImageLoading(false);
        if (!imageUploadData.url) throw new Error("No URL returned");
        uploadedImageUrl = imageUploadData.url;
      } catch (err) {
        console.error(err);
        alert("⚠️ Review image upload failed. Please try again.");
        setReviewImageLoading(false);
        return;
      }
    }

    const newReview = {
      productId: product.id,
      user: reviewName,
      rating: reviewRating,
      text: reviewText,
      name: reviewName,
      image: uploadedImageUrl,
    };

    try {
      const res = await fetch("https://rangdesaaj.onrender.com/api/reviews", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newReview),
      });
      if (!res.ok) throw new Error("Failed to submit review");
      const savedReview = await res.json();

      setReviews([savedReview, ...reviews]);
      setReviewText("");
      setReviewName("");
      setReviewRating(0);
      setReviewImage(null);
      setReviewImagePreview(null);
      alert("Thank you for your review!");
    } catch (err) {
      console.error(err);
      alert("Failed to submit review. Please try again.");
      setReviewImageLoading(false);
    }
  };

  // Handle customization form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomizeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle customization form image change
  const handleFormImageChange = (e) => {
    setCustomizeData((prev) => ({
      ...prev,
      image: e.target.files[0],
    }));
  };

  // Razorpay script loader helper
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById("razorpay-script")) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.id = "razorpay-script";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Handle submit order with Razorpay
  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    let uploadedImageUrl = null;

    if (customizeData.image) {
      const formData = new FormData();
      formData.append("image", customizeData.image);
      setFormLoading(true);
      try {
        const imageUploadRes = await fetch("https://rangdesaaj.onrender.com/api/upload-image", {
          method: "POST",
          body: formData,
        });
        const imageUploadData = await imageUploadRes.json();
        if (!imageUploadData.url) throw new Error("No URL returned");
        uploadedImageUrl = imageUploadData.url;
      } catch (err) {
        console.error(err);
        setFormLoading(false);
        alert("⚠️ Image upload failed. Try again.");
        return;
      }
    }

    const res = await loadRazorpayScript();
    if (!res) {
      alert("Razorpay SDK failed to load.");
      return;
    }

    let orderData;
    try {
      const orderRes = await fetch("https://rangdesaaj.onrender.com/api/create-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: product.price * customizeData.Quantity * 100,
          productName: product.name,
          email: customizeData.email,
          currency: "INR",
        }),
      });
      orderData = await orderRes.json();
    } catch (err) {
      console.error(err);
      alert("❌ Failed to create Razorpay order");
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: product.price * customizeData.Quantity * 100,
      currency: orderData.currency,
      name: "RangDesaaj",
      description: product.name,
      image: "/logo.png",
      order_id: orderData.id,
      handler: async function (response) {
        try {
          const verifyRes = await fetch("https://rangdesaaj.onrender.com/api/verify-payment", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...customizeData,
              imageUrl: uploadedImageUrl,
              razorpayOrderId: orderData.id,
              razorpay_payment_id: response.razorpay_payment_id,
              amount: product.price,
              currency: orderData.currency,
              productName: product.name,
            }),
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            alert("🎉 Payment & Order Successful!");
            setFormLoading(false);
            setShowCustomizeForm(false);
            navigate("/");
          } else {
            alert("⚠️ Payment succeeded, but order storing failed.");
            setFormLoading(false);
          }
        } catch (err) {
          console.error(err);
          alert("❌ Payment received but order verification failed.");
          setFormLoading(false);
        }
      },
      prefill: {
        name: customizeData.name,
        email: customizeData.email,
        contact: customizeData.whatsapp,
      },
      notes: {
        message: customizeData.message,
        colors: customizeData.colors,
        size: customizeData.size,
        text: customizeData.text,
        Quantity: customizeData.Quantity,
      },
      theme: {
        color: "#000000",
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // Render stars for ratings
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={`star-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <StarHalf key="half-star" className="w-5 h-5 fill-yellow-400 text-yellow-400" />
      );
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Star key={`empty-star-${i}`} className="w-5 h-5 text-gray-300" />
      );
    }

    return stars;
  };

  const averageRating =
    reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : 0;

  const totalReviews = reviews.length;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  // Product not found state
  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Product not found.</p>
      </div>
    );
  }
  
const images = product.images && product.images.length > 0 ? product.images : ["/placeholder.svg"];
  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <a href="/" className="hover:text-gray-900">
              Home
            </a>
            <span>/</span>
            <a href="/shop" className="hover:text-gray-900">
              Shop
            </a>
            <span>/</span>
            <span className="text-gray-900 font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Info Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Images */}
            <div className="space-y-4">
              <div className="bg-gray-100 rounded-lg overflow-hidden aspect-square">
                <img
                  src={images[mainImageIndex]}
                  onClick={() => setPopupImage(images[mainImageIndex])}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                {product.images?.length > 0 ? (
                  product.images.map((image, index) => (
                    <div
                      key={index}
                      onClick={() => setMainImageIndex(index)}
                      className={`bg-gray-100 rounded-lg overflow-hidden aspect-square cursor-pointer transition-all hover:ring-2 hover:ring-black ${
                index === mainImageIndex ? "ring-2 ring-black" : ""
              }`}
                      
                    >
                      <img
                        src={image || "/placeholder.svg"}
                        alt={`${product.name} view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))
                ) : (
                  <div className="col-span-3 text-center text-gray-600">
                    No additional images available.
                  </div>
                )}
              </div>
            </div>
            
            {/* Details */}
            <div className="space-y-6">
              <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
              <div className="flex items-center space-x-2">
                <div className="flex">{renderStars(parseFloat(averageRating))}</div>
                <span className="text-gray-600">
                  {averageRating} ({totalReviews} reviews)
                </span>
              </div>
              <p className="text-3xl font-bold text-gray-900">₹{product.price}</p>
              <div className="border-t border-b border-gray-200 py-6">
                <p className="text-gray-600 leading-relaxed">{product.description}</p>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Features:</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  {product.features?.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  )) || <li>No features available.</li>}
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Color:</h3>
                <div className="flex flex-wrap gap-3">
                  {product.colors?.map((color, index) => (
                    <button
                      key={index}
                      className={`px-4 py-2 border rounded-md ${
                        index === 0
                          ? "border-black bg-gray-100"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {color}
                    </button>
                  )) || <p className="text-gray-600">No colors available.</p>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={() => setShowCustomizeForm(true)}
                  className="flex-1 bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                >
                  Buy Now
                </button>
              </div>
        {/*}      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <p className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                    />
                  </svg>
                  Free shipping on orders over $50
                </p>
                <p className="flex items-center mt-2">
                  <svg
                    className="w-5 h-5 mr-2 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Ships within 2-3 business days
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews</h2>

          {/* Reviews Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center space-x-4 mb-4 md:mb-0">
                <div className="flex">{renderStars(parseFloat(averageRating))}</div>
                <div>
                  <span className="text-2xl font-bold text-gray-900">{averageRating}</span>
                  <span className="text-gray-600 ml-1">out of 5</span>
                </div>
              </div>
              <div>
                <span className="text-gray-600">Based on {totalReviews} reviews</span>
              </div>
            </div>
          </div>

          {/* Write a Review Form */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Write a Review</h3>
            <form onSubmit={handleReviewSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating *</label>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => handleRatingClick(rating)}
                      onMouseEnter={() => setHoverRating(rating)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 focus:outline-none"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          hoverRating || reviewRating >= rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="reviewName" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="reviewName"
                  name="reviewName"
                  value={reviewName}
                  onChange={(e) => setReviewName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="review" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review *
                </label>
                <textarea
                  id="review"
                  rows={4}
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="Share your experience with this product..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add Photos (Optional)</label>
                <div className="flex items-center space-x-4">
                  <label className="cursor-pointer bg-gray-100 border border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-gray-200 transition-colors">
                    <svg
                      className="w-8 h-8 text-gray-500 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-sm text-gray-600">Upload Photo</span>
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                  </label>
                  {reviewImagePreview && (
                    <div className="relative">
                      <img src={reviewImagePreview || "/placeholder.svg"} alt="Review" className="w-24 h-24 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => {
                          setReviewImage(null);
                          setReviewImagePreview(null);
                        }}
                        className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-sm"
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={reviewImageLoading}
                className={`bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors ${
                  reviewImageLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {reviewImageLoading ? "Uploading Image..." : "Submit Review"}
              </button>
            </form>
          </div>

          {/* Reviews List */}
          <div className="space-y-6">
            {reviews.length > 0 ? (
              reviews.map((review) => (
                <div key={review.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 font-medium">{review.user.charAt(0)}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{review.user}</h4>
<p className="text-sm text-gray-500">
  {review.date ? new Date(review.date).toISOString().split("T")[0] : "No date"}
</p>

                      </div>
                    </div>
                    <div className="flex">{renderStars(review.rating)}</div>
                  </div>
                  <div className="mt-4">
                    <p className="text-gray-600">{review.text}</p>
                    {review.image && (
<div className="mt-4">
  <img
    src={review.image || "/placeholder.svg"}
    alt="Review"
    className="w-32 h-32 object-cover rounded-lg cursor-pointer"
    onClick={() => setPopupImage(review.image)}
  />
</div>
                    )}
                  </div>
{popupImage && (
  <div
    className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
    onClick={() => setPopupImage(null)}
  >
    <img
      src={popupImage}
      alt="Enlarged Review"
      className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-lg"
      onClick={(e) => e.stopPropagation()}
    />
    <button
      onClick={() => setPopupImage(null)}
      className="absolute top-4 right-4 text-white bg-black bg-opacity-70 rounded-full p-2 hover:bg-opacity-90"
      aria-label="Close image popup"
    >
      ✕
    </button>
  </div>
)}
                  <div className="mt-4 flex items-center space-x-4">
                    <button className="flex items-center text-sm text-gray-500 hover:text-gray-700">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                        />
                      </svg>
                      Helpful ({review.helpful})
                    </button>
                    <button className="text-sm text-gray-500 hover:text-gray-700">Report</button>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                <p className="text-gray-600">No reviews yet. Be the first to review this product!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Customize Form Modal */}
{/* Customize Form Modal */}
{showCustomizeForm && product && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Customize Your Order</h2>
        <button
          onClick={() => setShowCustomizeForm(false)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close customize form"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="px-6 py-4 bg-gray-50 border-b">
        <div className="flex items-center space-x-4">
          <img src={product.images?.[0] || "/placeholder.svg"} alt={product.name} className="w-16 h-16 rounded-lg object-cover" />
          <div>
            <h3 className="font-bold text-gray-900">{product.name}</h3>
            <p className="text-xl font-bold text-black">₹{product.price}</p>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmitOrder} className="p-6 space-y-6">
        {/* Customize form inputs */}
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <h3 className="text-lg font-bold text-gray-900 mb-2">{"Let's Customize Together! 🎨"}</h3>
          <p className="text-gray-600 text-sm">
            {"Tell us how you'd like to customize this piece - colors, text, size, or any special requests!"}
          </p>
        </div>

        {/* Example input fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={customizeData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={customizeData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>
        </div>
                <div>
          <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-2">
            WhatsApp (Optional)
          </label>
          <input
          type="tel"
          id="whatsapp"
          name="whatsapp"
          value={customizeData.whatsapp}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
          />
      </div>

        {/* Conditional Preferred Colors Input */}
        {product.needsColor && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="colors" className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Colors
              </label>
              <input
                type="text"
                id="colors"
                name="colors"
                value={customizeData.colors}
                onChange={handleInputChange}
                placeholder="e.g., Blue, Gold, White"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>
          </div>
        )}
            {product.needsCustomText && (
      <div>
        <label htmlFor="text" className="block text-sm font-medium text-gray-700 mb-2">
          Custom Text (if applicable)
        </label>
        <input
        type="text"
        id="text"
        name="text"
        value={customizeData.text}
        onChange={handleInputChange}
        placeholder="Any text you'd like added to the piece"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
        />
    </div>
  )}
  {product.needsRequest && (
    <div>
      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
        Special Requests & Additional Details *
      </label>
      <textarea
        id="message"
        name="message"
        required
        rows={4}
        value={customizeData.message}
        onChange={handleInputChange}
        placeholder="Tell us about any special requests, design ideas, or inspiration you have for this piece..."
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
        />
    </div>
  )}
  <div>
    <label htmlFor="Quantity" className="block text-sm font-medium text-gray-700 mb-2">
      Quantity *
    </label>
    <input
    type="tel"
    id="Quantity"
    name="Quantity"
    required
    value={customizeData.Quantity}
    onChange={handleInputChange}
    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
    />
</div>

        {/* Other inputs */}
        {product.needsImage && (
        <div>
          <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
            Reference Image (Optional)
          </label>
          <input
            type="file"
            id="image"
            name="image"
            accept="image/*"
            onChange={handleFormImageChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">
            Upload any reference images or inspiration photos
          </p>
        </div>
        )}
        <hr />
<h4 className="font-semibold text-gray-900 mb-2">Delivery Address</h4>
<div>
<label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
Name *
</label>
<input
type="text"
id="name"
name="name"
required
value={customizeData.name}
onChange={handleInputChange}
className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
/>
</div>
<div>
<label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
State *
</label>
<input
type="text"
id="state"
name="state"
required
value={customizeData.state}
onChange={handleInputChange}
className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
/>
</div>
<div>
<label htmlFor="district" className="block text-sm font-medium text-gray-700 mb-2">
District *
</label>
<input
type="text"
id="district"
name="district"
required
value={customizeData.district}
onChange={handleInputChange}
className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
/>
</div>
<div>
<label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
Pin Code *
</label>
<input
type="tel"
id="pin"
name="pin"
required
value={customizeData.pin}
onChange={handleInputChange}
className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
/>
</div>
<div>
<label htmlFor="flat" className="block text-sm font-medium text-gray-700 mb-2">
House/Flat No *
</label>
<input
type="tel"
id="flat"
name="flat"
required
value={customizeData.flat}
onChange={handleInputChange}
className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
/>
</div>
<div>
<label htmlFor="apartment" className="block text-sm font-medium text-gray-700 mb-2">
Apartment Name
</label>
<input
type="text"
id="apartment"
name="apartment"
value={customizeData.apartment}
onChange={handleInputChange}
className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
/>
</div>
<div>
<label htmlFor="area" className="block text-sm font-medium text-gray-700 mb-2">
Area *
</label>
<input
type="text"
id="area"
name="area"
required
value={customizeData.area}
onChange={handleInputChange}
className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
/>
</div>
<div>
<label htmlFor="instructions" className="block text-sm font-medium text-gray-700 mb-2">
Instructions to First Delivery *
</label>
<textarea
id="instructions"
name="instructions"
required
rows={4}
value={customizeData.instructions}
onChange={handleInputChange}
className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
/>
</div>

        {/* Pricing Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-2">Pricing Information</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p>• Selling Price: ₹{product.price}</p>
            <p>• MRP: ₹{product.mainPrice}</p>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="button"
            onClick={() => setShowCustomizeForm(false)}
            className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={formLoading}
            className={`flex-1 py-3 px-6 rounded-lg font-medium transition-colors ${
              formLoading ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800 text-white"
            }`}
          >
            {formLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              "Place Custom Order"
            )}
          </button>
        </div>
      </form>
    </div>
  </div>
)}
    </div>
  );
};

export default ProductDetail;